/**
 * Header Maestro — Barra superior (64 px), solo tokens.
 *
 * Izquierda : Selector de empresa + RUC · régimen
 * Centro    : Filtro Período (global)
 * Derecha   : Contador/Emprendedor + Alertas + ES|EN + Tema + Menú de usuario (Sugerencias, salir)
 */

import { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import {
  Calendar, ChevronDown,
  Sun, Moon, Menu, Bell, MoreHorizontal,
} from 'lucide-react';
import UserMenu from './UserMenu';
import LangToggle from './LangToggle';
import { useAlertsContext } from '../../context/AlertsContext';
import { useTheme } from '../../context/ThemeContext';
import { useCompanyContext } from '../../context/CompanyContext';
import { useLang } from '../../context/LangContext';
import { t } from '../../i18n/dictionary';
import { useUserRole } from '../../context/UserRoleContext';

export default function Header({ onMenuToggle }: { onMenuToggle?: () => void }) {
  const { activeCompany, setActiveCompany, filterPeriodo, setFilterPeriodo, isFilterPeriodoClosed, companies, periodMode, setPeriodMode, filterYear, setFilterYear } = useCompanyContext();
  const { theme, toggle: toggleTheme } = useTheme();
  const { viewMode } = useUserRole();
  const { lang } = useLang();
  const isDark = theme === 'dark';

  const MESES_SHORT = ['', ...t('time.monthsShort', lang).split(',')];

  const REGIMEN_LABELS: Record<string, string> = {
    general: t('header.regGeneral', lang),
    mype:    t('header.regMype', lang),
    rer:     t('header.regRer', lang),
    nrus:    t('header.regNrus', lang),
  };

  const { alertCount, openPanel } = useAlertsContext();

  // Company dropdown
  const [companyOpen, setCompanyOpen] = useState(false);

  // Móvil y tablet (≤ 1023 px): los controles de la derecha viven en un menú de desborde (botón ⋯)
  const [moreOpen, setMoreOpen] = useState(false);

  // Period filter
  const [yearDraft, setYearDraft] = useState('');

  // Month picker popover — se renderiza por portal (position: fixed) porque vive
  // dentro del contenedor con overflow-x-auto del filtro, que lo recortaría.
  const [monthPickerOpen, setMonthPickerOpen] = useState(false);
  const [monthPickerPos, setMonthPickerPos] = useState<{ top: number; left: number } | null>(null);
  const monthPickerRef = useRef<HTMLDivElement>(null);
  const monthPopoverRef = useRef<HTMLDivElement>(null);
  const monthBtnRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (!monthPickerOpen) return;
    function handleOutside(e: MouseEvent) {
      const target = e.target as Node;
      if (monthPickerRef.current?.contains(target) || monthPopoverRef.current?.contains(target)) return;
      setMonthPickerOpen(false);
    }
    // Posición fija calculada al abrir: si el header hace scroll o cambia el ancho, se cierra.
    function close() { setMonthPickerOpen(false); }
    document.addEventListener('mousedown', handleOutside);
    window.addEventListener('resize', close);
    window.addEventListener('scroll', close, true);
    return () => {
      document.removeEventListener('mousedown', handleOutside);
      window.removeEventListener('resize', close);
      window.removeEventListener('scroll', close, true);
    };
  }, [monthPickerOpen]);

  function handleYearBlur() {
    const y = parseInt(yearDraft, 10);
    if (y >= 2000 && y <= 2099) {
      setFilterPeriodo(`${y}-${filterPeriodo.split('-')[1]}`);
    }
    setYearDraft('');
  }

  const initials = activeCompany?.name
    ? activeCompany.name.split(' ').slice(0, 2).map(w => w[0]).join('').toUpperCase()
    : 'QP';

  return (
    <>
    <header className="hd z-20">
      {/* ── Hamburger (mobile only) ──────────────────────────────── */}
      <button
        onClick={onMenuToggle}
        className="hd-btn is-icon fx md:hidden flex-shrink-0"
        aria-label={t('header.openMenu', lang)}
        title={t('header.openMenu', lang)}
      >
        <Menu size={16} aria-hidden="true" />
      </button>

      {/* ── Selector de empresa ──────────────────────────────────── */}
      <div className="relative flex-shrink-0 hd-company-wrap">
        <button
          onClick={() => setCompanyOpen(!companyOpen)}
          className="hd-company fx"
          aria-haspopup="listbox"
          aria-expanded={companyOpen}
          aria-label={t('header.selectCompany', lang)}
        >
          <div className="hd-avatar-co" aria-hidden="true">{initials}</div>

          <div className="block text-left min-w-0">
            <p className="truncate max-w-[140px] sm:max-w-[200px] md:max-w-none" style={{ fontSize: 'var(--type-body-size)', lineHeight: 'var(--type-body-line)', fontWeight: 600, color: 'var(--ink)' }}>
              {activeCompany?.name ?? t('header.noCompany', lang)}
            </p>
            {(activeCompany?.ruc || activeCompany?.regimen) && (
              <p className="hidden md:block truncate" style={{ fontSize: 'var(--type-caption-size)', lineHeight: 'var(--type-caption-line)', fontWeight: 400, color: 'var(--muted)' }}>
                {activeCompany?.ruc && <span className="font-mono">RUC {activeCompany.ruc}</span>}
                {activeCompany?.ruc && activeCompany?.regimen && ' · '}
                {activeCompany?.regimen && (REGIMEN_LABELS[activeCompany.regimen] ?? activeCompany.regimen)}
              </p>
            )}
          </div>

          <ChevronDown size={14} aria-hidden="true" className={`flex-shrink-0 transition-transform ${companyOpen ? 'rotate-180' : ''}`} style={{ color: 'var(--muted)' }} />
        </button>

        {/* Dropdown */}
        {companyOpen && companies.length > 0 && (
          <>
            <div className="fixed inset-0 z-40" onClick={() => setCompanyOpen(false)} aria-hidden="true" />
            <div role="listbox" className="menu-pop absolute top-full left-0 mt-1.5 w-72 z-50 py-1.5">
              {companies.map((c) => {
                const isSelected = c.companyId === activeCompany?.companyId;
                const ci = (c.name ?? '').split(' ').slice(0, 2).map(w => w[0] ?? '').join('').toUpperCase();
                return (
                  <button
                    key={c.companyId}
                    role="option"
                    aria-selected={isSelected}
                    onClick={() => { setActiveCompany(c); setCompanyOpen(false); }}
                    className={`menu-row${isSelected ? ' is-selected' : ''}`}
                  >
                    <div className="hd-avatar-co flex-shrink-0" style={{ width: 24, height: 24 }} aria-hidden="true">{ci}</div>
                    <div className="min-w-0 flex-1">
                      <p className="truncate" style={{ fontSize: 'var(--type-small-size)', fontWeight: 600 }}>{c.name}</p>
                      <p className="font-mono" style={{ fontSize: 'var(--type-caption-size)', color: 'var(--muted)' }}>
                        RUC {c.ruc}
                      </p>
                    </div>
                    {isSelected && (
                      <span aria-hidden="true" style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--brand)' }} />
                    )}
                  </button>
                );
              })}
            </div>
          </>
        )}
      </div>

      {/* tq88: solo los controles de la derecha hacen scroll horizontal en ventanas
          angostas. El selector de empresas y el menú de usuario quedan FUERA: overflow-x != visible
          fuerza overflow-y a auto y recortaría sus desplegables (absolute top-full). ml-auto
          reemplaza al spacer: empuja a la derecha cuando cabe, y es 0 cuando desborda. */}
      <button
        type="button"
        onClick={() => setMoreOpen(v => !v)}
        className="hd-btn is-icon fx lg:hidden flex-shrink-0 ml-auto"
        aria-label={t('header.moreControls', lang)}
        title={t('header.moreControls', lang)}
        aria-expanded={moreOpen}
      >
        <MoreHorizontal size={16} aria-hidden="true" />
      </button>
      {moreOpen && <div className="fixed inset-0 z-20 lg:hidden" onClick={() => setMoreOpen(false)} aria-hidden="true" />}
      <div className={`hd-controls${moreOpen ? ' is-open' : ''}`}>
      <div className="hd-controls-inner">

      {/* ── Filtro de Período ────────────────────────────────────── */}
      {activeCompany && (
        <div className="flex items-center gap-2 flex-shrink-0">
          {/* [Mes] [Año] toggle */}
          <div className="seg" role="group" aria-label={t('nav.periodo', lang)}>
            <button onClick={() => setPeriodMode('month')} aria-pressed={periodMode === 'month'}>
              {t('shell.month', lang)}
            </button>
            <button onClick={() => setPeriodMode('year')} aria-pressed={periodMode === 'year'}>
              {t('shell.year', lang)}
            </button>
          </div>

          {/* Period selector */}
          <div className="hd-field">
            <Calendar size={12} aria-hidden="true" style={{ color: 'var(--muted)' }} />

            {periodMode === 'month' ? (
              <>
                {/* Custom month picker — replaces native select for consistent brand highlight */}
                <div ref={monthPickerRef} className="relative">
                  <button
                    ref={monthBtnRef}
                    onClick={() => {
                      if (monthPickerOpen) { setMonthPickerOpen(false); return; }
                      const r = monthBtnRef.current?.getBoundingClientRect();
                      if (r) setMonthPickerPos({ top: r.bottom + 8, left: r.left + r.width / 2 });
                      setMonthPickerOpen(true);
                    }}
                    className="hd-plain leading-none"
                    aria-haspopup="true"
                    aria-expanded={monthPickerOpen}
                    title={t('header.filterByMonth', lang)}
                  >
                    {MESES_SHORT[parseInt(filterPeriodo.split('-')[1] ?? '1', 10)]}
                  </button>
                  {monthPickerOpen && monthPickerPos && createPortal(
                    <div
                      ref={monthPopoverRef}
                      className="menu-pop p-2"
                      style={{
                        position: 'fixed',
                        top: monthPickerPos.top,
                        left: monthPickerPos.left,
                        zIndex: 50,
                        transform: 'translateX(-50%)',
                        display: 'grid',
                        gridTemplateColumns: 'repeat(3, 56px)',
                        gap: 2,
                      }}
                    >
                      {Array.from({ length: 12 }, (_, i) => {
                        const m = String(i + 1).padStart(2, '0');
                        const isSelected = filterPeriodo.split('-')[1] === m;
                        const selectedTextColor = 'var(--on-brand)';
                        const defaultTextColor  = 'var(--body)';
                        return (
                          <button
                            key={m}
                            onClick={() => { setFilterPeriodo(`${filterPeriodo.split('-')[0]}-${m}`); setMonthPickerOpen(false); }}
                            style={{
                              height: 32, borderRadius: 6, border: 'none',
                              fontSize: 'var(--type-small-size)', fontWeight: isSelected ? 600 : 500,
                              background: isSelected ? 'var(--brand)' : 'transparent',
                              color: isSelected ? selectedTextColor : defaultTextColor,
                              transition: 'background 120ms, color 120ms',
                            }}
                            onMouseEnter={e => { if (!isSelected) { (e.currentTarget as HTMLButtonElement).style.background = 'var(--brand-soft)'; (e.currentTarget as HTMLButtonElement).style.color = 'var(--brand)'; } }}
                            onMouseLeave={e => { if (!isSelected) { (e.currentTarget as HTMLButtonElement).style.background = 'transparent'; (e.currentTarget as HTMLButtonElement).style.color = defaultTextColor; } }}
                          >
                            {MESES_SHORT[i + 1]}
                          </button>
                        );
                      })}
                    </div>,
                    document.body,
                  )}
                </div>
                <input
                  type="text"
                  value={yearDraft || filterPeriodo.split('-')[0]}
                  onChange={(e) => setYearDraft(e.target.value)}
                  onBlur={handleYearBlur}
                  onFocus={(e) => setYearDraft(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.currentTarget.blur(); } }}
                  className="hd-input w-11"
                  aria-label={t('header.yearEditHint', lang)}
                  title={t('header.yearEditHint', lang)}
                />
              </>
            ) : (
              <input
                type="text"
                value={yearDraft || filterYear}
                onChange={(e) => setYearDraft(e.target.value)}
                onBlur={() => {
                  const y = parseInt(yearDraft, 10);
                  if (y >= 2000 && y <= 2099) setFilterYear(String(y));
                  setYearDraft('');
                }}
                onFocus={(e) => setYearDraft(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
                className="hd-input w-14"
                aria-label={t('shell.year', lang)}
                title={t('shell.year', lang)}
              />
            )}
          </div>
        </div>
      )}

      {/* ── Período Cerrado badge ────────────────────────────────── */}
      {isFilterPeriodoClosed && (
        <span className="hd-closed hidden md:inline-flex">
          {t('header.periodClosed', lang)}
        </span>
      )}

      {/* ── Contador / Emprendedor (solo visual; la lógica de rol y la recarga no cambian) ── */}
      <div className="seg" role="group" aria-label={t('role.vistaPerfil', lang)}>
        <button
          onClick={() => { localStorage.setItem('quipus_view_mode', 'contador'); window.location.href = '/contador'; }}
          aria-pressed={viewMode === 'contador'}
          aria-label={t('role.contador', lang)}
        >
          <span className="sm:hidden">{t('role.contadorShort', lang)}</span>
          <span className="hidden sm:inline">{t('role.contador', lang)}</span>
        </button>
        <button
          onClick={() => { localStorage.setItem('quipus_view_mode', 'emprendedor'); window.location.href = '/'; }}
          aria-pressed={viewMode === 'emprendedor'}
          aria-label={t('role.emprendedor', lang)}
        >
          <span className="sm:hidden">{t('role.emprendedorShort', lang)}</span>
          <span className="hidden sm:inline">{t('role.emprendedor', lang)}</span>
        </button>
      </div>

      {/* ── Bell / Alerts ────────────────────────────────────── */}
      <button
        onClick={openPanel}
        title={t('header.alerts', lang)}
        aria-label={t('header.alerts', lang)}
        className="hd-btn is-icon fx flex-shrink-0"
      >
        <Bell size={14} aria-hidden="true" />
        {alertCount > 0 && (
          <span className="hd-badge">{alertCount > 99 ? '99+' : alertCount}</span>
        )}
      </button>

      {/* TODO post-beta: re-enable when BackupService migrated to Supabase */}

      {/* ── Idioma ES | EN (visible, junto al tema) ─────────────────── */}
      <LangToggle />

      {/* ── Theme Toggle ─────────────────────────────────────────── */}
      <button
        onClick={toggleTheme}
        title={isDark ? t('header.lightMode', lang) : t('header.darkMode', lang)}
        aria-label={isDark ? t('header.lightMode', lang) : t('header.darkMode', lang)}
        className="hd-btn is-icon fx flex-shrink-0"
      >
        {isDark ? <Sun size={14} aria-hidden="true" /> : <Moon size={14} aria-hidden="true" />}
      </button>
      </div>
      </div>

      {/* ── Menú de usuario (avatar): idioma ES/EN + cerrar sesión ─── */}
      <UserMenu />
    </header>
    </>
  );
}
